import Button from "components/UI/Button";
import DropDown, { DropDownMenuItem } from "components/UI/DropDown";
import SearchBar from "components/UI/SearchBar";

import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { PlusIcon, FolderIcon } from "@heroicons/react/solid";
import { ResourceCenterTable, FileCards } from "features/ResourceCenter";
import pdfDocIcon from "images/icons/pdf-doc-icon.png";
import msExcelSheetDocIcon from "images/icons/excel-sheet-icon.png";
import mswordDocIcon from "images/icons/microsoft-word-document-icon.png";
import AccentTitle from "components/UI/AccentTitle";
import { ApplicationNav } from "../ApplicationNav";
import { useParams, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useFileUpload } from "hooks/useFileUpload";

import { signAsset } from "api/assets";

import { getAcceptedFileTypes, sanitizeResponseError } from "utils";
import { notifyUser } from "store/Toast";
import { postResourceFiles } from "api/Rover/ApplicationForm";
import { IPostResourceFilesService } from "api/Rover/ApplicationForm/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import UploadButton from "components/UI/Button/UploadButton";
import { ChangeEvent, useState } from "react";
import { useResource } from "hooks/ReactQueryHooks/useApplicationFromQuery";
import { RoverReactQueryKey } from "constants/ReactQueryKeys";
import UnAuthorizedMessage from "components/Permission/UnauthorizedMessage";
import { EPermission } from "types/Permission";
import { usePermission } from "hooks/usePermission";

export const ApplicationResources = () => {
  const { applicationFormId } = useParams<string>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [sortOrder, setSortOrder] = useState<{
    order: undefined | string;
    orderBy: undefined | string;
  }>({
    order: "asc",
    orderBy: "name",
  });

  const [fetchParams, setFetchParams] = useState({
    page: 1,
    perPage: 10,
  });

  const queryClient = useQueryClient();

  const { upload } = useFileUpload({ signAsset: signAsset });
  const { hasPermission } = usePermission();

  const RecentlyAccessed: any[] = [];
  const Folders: any[] = [];

  const fetchResourceFiles = useResource({
    formId: applicationFormId!,
    params: { ...fetchParams, ...sortOrder },
  });
  const data = fetchResourceFiles?.data?.data;

  const totalPage = Math.ceil(
    fetchResourceFiles.data?.meta.count / fetchParams.perPage
  );

  const onPageChangeHandler = (direction: string) => {
    if (direction === "prev") {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page > 1 ? prevState.page - 1 : 1,
      }));
    } else if (direction === "next") {
      setFetchParams((prevState) => ({
        ...prevState,
        page: prevState.page === totalPage ? totalPage : prevState.page + 1,
      }));
    }
  };
  const perPageHandler = (e: ChangeEvent<HTMLSelectElement>) => {
    setFetchParams((prevState) => ({
      ...prevState,
      page: 1,
      perPage: Number(e.target.value),
    }));
  };

  const sortHandler = ({
    orderBy,
    order,
  }: {
    orderBy: string;
    order: string;
  }) => {
    setSortOrder({
      order,
      orderBy,
    });
  };

  const uploadResourceAsset = useMutation(
    (payload: IPostResourceFilesService) => postResourceFiles(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([RoverReactQueryKey.GET_RESOURCE]);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: "File uploaded successfully",
            toastType: "success",
          })
        );
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const uploadFile = useMutation(
    (payload: {
      signAssetPayload: {
        key: string;
        type: string;
        resource: string;
      }[];
      files: File[];
    }) => upload(payload),
    {
      onSuccess: (response) => {
        const assetsFiles = response?.files.map((file: any, index: number) => {
          return {
            name: file.name,
            key: response?.sign.fKey ?? response.sign[index].fKey,
            contentType: file.type,
            assetType: "file",
            size: file.size,
          };
        });
        uploadResourceAsset.mutate({
          data: {
            assets: assetsFiles,
          },
          applicationFormId: applicationFormId!,
        });
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: "failed",
          })
        );
      },
    }
  );

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles: File[] = [...e.target.files];
      let validFiles: File[] = [];
      const invalidFiles: string[] = [];

      selectedFiles.forEach((file: File) => {
        const acceptedFileTypes = getAcceptedFileTypes().split(",");
        if (acceptedFileTypes.includes(file.type)) {
          validFiles.push(file);
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: `${invalidFiles.join(
              ", "
            )} file types are not accepted to upload.`,
            toastType: "failed",
          })
        );
      }

      if (validFiles.length > 0) {
        const signPayload = validFiles.map<{
          key: string;
          type: string;
          resource: string;
        }>((file) => ({
          key: file.name,
          type: file.type,
          resource: "applicationForm",
        }));

        uploadFile.mutate({
          signAssetPayload: signPayload,
          files: validFiles,
        });
      }
    }
  };

  if (!hasPermission([EPermission.LIST_APPLICATION_FORM_RESOURCES])) {
    return (
      <UnAuthorizedMessage
        title="Unauthorized"
        message="You do not have permission to view this application form resources."
      />
    );
  }

  return (
    <>
      <ApplicationNav />
      <div className="w-full border bg-white rounded">
        <div className="flex justify-between w-full items-center pr-5 border-b">
          <AccentTitle title="Resources" />
          {hasPermission([EPermission.UPDATE_APPLICATION_FORM_RESOURCES]) && (
            <div className="flex justify-between items-center">
              {/* <SearchBar /> */}
              <UploadButton
                icon={<PlusIcon className="h-5 w-5" />}
                name="files"
                fileTypesAccepted={getAcceptedFileTypes()}
                multipleFiles={true}
                customStyle="text-white rounded bg-primaryColor-normal hover:bg-primaryColor-medium"
                changeHandler={handleFileUpload}
                label={`${
                  uploadFile.isLoading || uploadResourceAsset.isLoading
                    ? "Loading..."
                    : "Upload File"
                }`}
              />
            </div>
          )}
        </div>
        {/* <div className='w-full px-5'>
          <FileCards title='Recently Accessed' files={RecentlyAccessed} />
          <FileCards title='Folders Accessed' files={Folders} />
        </div> */}
        <div className="w-full px-5 my-4">
          <div className="flex justify-between items-center py-3">
            <h4 className="text-sm font-semibold flex items-center justify-center">
              <Link
                to="/app/rover/fbdi/resources"
                className="hover:underline cursor-pointer"
              >
                All Files
              </Link>
              {searchParams.get("folder") && (
                <>
                  <span>
                    <ChevronRightIcon className="h-5 w-5" />
                  </span>
                  <span>{searchParams.get("folder")}</span>
                </>
              )}
            </h4>
            {/* <div className='flex gap-2 items-center'>
              <DropDown menuItems={[{}] as DropDownMenuItem[]}>
                <div
                  id='approval-actions-dropdown'
                  className='flex gap-2 items-center border p-2 rounded-md w-40 justify-between font-medium text-neutralColor-normal'
                >
                  <span className='text-sm'>Sort by</span>
                  <ChevronDownIcon className='h-4 w-4' />
                </div>
              </DropDown>
            </div> */}
          </div>
          <ResourceCenterTable
            data={data}
            pageSize={totalPage}
            page={fetchParams.page}
            perPage={fetchParams.perPage}
            perPageHandler={perPageHandler}
            pageChangeHandler={onPageChangeHandler}
            sortHandler={sortHandler}
          />
        </div>
      </div>
    </>
  );
};
